import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, BookOpen, GraduationCap, ClipboardList, BarChart3, Users, Settings, LogOut, MessageCircle, Upload, FileText, ShieldCheck, Brain, Code2, Video, Info, Trophy, Mail } from "lucide-react";
import { getUser, logout } from "@/lib/api";

const studentItems = [
  { to: "/student", label: "Overview", icon: LayoutDashboard, testid: "nav-student-overview" },
  { to: "/student/courses", label: "Courses", icon: BookOpen, testid: "nav-student-courses" },
  { to: "/student/enrolled", label: "My Learning", icon: GraduationCap, testid: "nav-student-enrolled" },
  { to: "/student/my-classes", label: "My Classes", icon: Video, testid: "nav-student-my-classes" },
  { to: "/student/assignments", label: "Assignments", icon: ClipboardList, testid: "nav-student-assignments" },
  { to: "/student/quizzes", label: "Quizzes", icon: FileText, testid: "nav-student-quizzes" },
  { to: "/student/games", label: "Brain Games", icon: Brain, testid: "nav-student-games" },
  { to: "/student/coding", label: "Coding Practice", icon: Code2, testid: "nav-student-coding" },
  { to: "/student/achievements", label: "Streak & Trophies", icon: Trophy, testid: "nav-student-achievements" },
  { to: "/student/analytics", label: "Analytics", icon: BarChart3, testid: "nav-student-analytics" },
  { to: "/about", label: "About", icon: Info, testid: "nav-student-about" },
  { to: "/contact", label: "Contact", icon: Mail, testid: "nav-student-contact" },
];

const staffItems = [
  { to: "/staff", label: "Overview", icon: LayoutDashboard, testid: "nav-staff-overview" },
  { to: "/staff/courses", label: "My Courses", icon: BookOpen, testid: "nav-staff-courses" },
  { to: "/staff/create", label: "Create Course", icon: Upload, testid: "nav-staff-create" },
  { to: "/staff/students", label: "Students", icon: Users, testid: "nav-staff-students" },
  { to: "/student/games", label: "Brain Games", icon: Brain, testid: "nav-staff-games" },
  { to: "/student/coding", label: "Coding Practice", icon: Code2, testid: "nav-staff-coding" },
  { to: "/staff/analytics", label: "Analytics", icon: BarChart3, testid: "nav-staff-analytics" },
  { to: "/about", label: "About", icon: Info, testid: "nav-staff-about" },
  { to: "/contact", label: "Contact", icon: Mail, testid: "nav-staff-contact" },
];

const adminItems = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, testid: "nav-admin-overview" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-admin-users" },
  { to: "/admin/courses", label: "Courses", icon: BookOpen, testid: "nav-admin-courses" },
  { to: "/admin/system", label: "System", icon: Settings, testid: "nav-admin-system" },
];

export default function Sidebar({ onOpenAI }) {
  const user = getUser();
  const nav = useNavigate();
  const items = user?.role === "staff" ? staffItems : user?.role === "admin" ? adminItems : studentItems;
  const roleBadge = user?.role || "guest";

  return (
    <aside className="sidebar-dark w-64 min-h-screen fixed left-0 top-0 z-30 flex flex-col" data-testid="sidebar">
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-white text-lg leading-tight">TrillionBytex<span className="text-blue-400">edu</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Smart TBX Learning Hub</div>
          </div>
        </div>
      </div>

      <div className="px-3 py-2 mt-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 px-3 mb-2">
          {roleBadge === "admin" && <span className="text-violet-400">Admin Console</span>}
          {roleBadge === "staff" && <span className="text-emerald-400">Staff Studio</span>}
          {roleBadge === "student" && <span className="text-blue-400">Student Panel</span>}
        </div>
        <nav className="space-y-1">
          {items.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors duration-200 ${
                  isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="mt-auto p-3">
        <button
          data-testid="open-ai-assistant-btn"
          onClick={onOpenAI}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 transition-colors duration-200 text-sm"
        >
          <MessageCircle className="w-4 h-4" />
          <span>Ask <span className="font-bold">02</span> (AI)</span>
          <span className="ml-auto w-2 h-2 rounded-full bg-violet-400 pulse-dot" />
        </button>
        {user?.role === "admin" && (
          <div className="mt-2 px-3 py-2 rounded-xl bg-slate-800/50 flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Root Access</span>
          </div>
        )}
        <button
          data-testid="logout-btn"
          onClick={() => { logout(); nav("/login"); }}
          className="mt-2 w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-colors duration-200 text-sm"
        >
          <LogOut className="w-4 h-4" />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}
